// components/StandaloneAlbumView.js
// Simplified album viewer for standalone (non-event-linked) albums - Web version
import React, { useState, useEffect, useCallback } from 'react';
import {
  FiFolder, FiChevronRight, FiArrowLeft, FiImage, FiCheck,
  FiCheckCircle, FiDownload, FiX, FiPlay, FiUser, FiBriefcase,
  FiClock, FiAlertTriangle
} from 'react-icons/fi';
import {
  getAlbumFolders,
  getAlbumMediaInFolder,
  getAlbumBrandingInfo,
  downloadFile,
} from '../services/albumService';
import PhotoLightbox from './PhotoLightbox';

const INITIAL_LOAD = 30;
const LOAD_MORE = 15;

const StandaloneAlbumView = ({ albumId, album, onMediaCountChange }) => {
  // Media state
  const [photos, setPhotos] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMorePhotos, setHasMorePhotos] = useState(true);

  // Folder navigation state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [viewMode, setViewMode] = useState('root');
  const [folderPath, setFolderPath] = useState([]);

  // Branding state
  const [brandingInfo, setBrandingInfo] = useState(null);

  // Photo viewer state
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Selection and download state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ completed: 0, total: 0 });

  // Album expiry warning state
  const [expiryDaysLeft, setExpiryDaysLeft] = useState(null);
  const EXPIRY_WARNING_DAYS = 7;

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [albumId]);

  // Check album expiry
  useEffect(() => {
    if (album?.storage_expires_at) {
      const expiryDate = new Date(album.storage_expires_at);
      const now = new Date();
      const diffTime = expiryDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= EXPIRY_WARNING_DAYS && diffDays >= 0) {
        setExpiryDaysLeft(diffDays);
      }
    }
  }, [album]);

  // Report media count to parent
  useEffect(() => {
    if (onMediaCountChange) {
      const photoCount = photos.filter(p => p.media_type !== 'video').length;
      const videoCount = photos.filter(p => p.media_type === 'video').length;
      onMediaCountChange({ photos: photoCount, videos: videoCount });
    }
  }, [photos, onMediaCountChange]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load branding info
      const brandingResult = await getAlbumBrandingInfo(albumId);
      if (brandingResult.success) {
        setBrandingInfo(brandingResult);
      }

      // Load folders
      const foldersResult = await getAlbumFolders(albumId);
      if (foldersResult.success) {
        setFolders(foldersResult.folders);
      }

      // Load root media
      await loadMedia(null);
    } catch (error) {
      console.error('Error loading album data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async (folderId = null) => {
    try {
      const mediaResult = await getAlbumMediaInFolder(albumId, folderId);

      if (mediaResult.success && mediaResult.media.length > 0) {
        const mapped = mediaResult.media.map(item => ({
          id: item.id,
          cdnUrl: item.cdn_url,
          thumbnail_cdn_url: item.thumbnail_cdn_url,
          uploaded_by: item.uploader_id,
          width: item.width,
          height: item.height,
          uploaded_at: item.uploaded_at,
          file_path: item.file_path,
          cdn_url: item.cdn_url,
          media_type: item.media_type,
        }));

        setAllMedia(mapped);
        setPhotos(mapped.slice(0, INITIAL_LOAD));
        setHasMorePhotos(mapped.length > INITIAL_LOAD);
        setSelectedFolder(folderId);
      } else {
        setAllMedia([]);
        setPhotos([]);
        setSelectedFolder(folderId);
        setHasMorePhotos(false);
      }
    } catch (error) {
      console.error('Error loading media:', error);
    }
  };

  const loadMorePhotos = () => {
    const nextBatch = allMedia.slice(photos.length, photos.length + LOAD_MORE);
    setPhotos([...photos, ...nextBatch]);
    setHasMorePhotos(photos.length + nextBatch.length < allMedia.length);
  };

  // Folder navigation
  const handleFolderSelect = (folder) => {
    setFolderPath([...folderPath, { id: folder.id, name: folder.folder_name }]);
    setViewMode('media');
    loadMedia(folder.id);
  };

  const handleBackFromFolder = () => {
    if (folderPath.length > 0) {
      const newPath = [...folderPath];
      newPath.pop();
      setFolderPath(newPath);

      if (newPath.length === 0) {
        setViewMode('root');
        loadMedia(null);
      } else {
        loadMedia(newPath[newPath.length - 1].id);
      }
    } else {
      setViewMode('root');
      loadMedia(null);
    }
  };

  // Photo press handler
  const handlePhotoPress = (index) => {
    if (selectionMode) {
      togglePhotoSelection(photos[index].id);
    } else {
      setViewerIndex(index);
      setViewerVisible(true);
    }
  };

  // Selection
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedPhotoIds(new Set());
    }
  };

  const togglePhotoSelection = (photoId) => {
    const newSelected = new Set(selectedPhotoIds);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotoIds(newSelected);
    if (newSelected.size === 0) {
      setSelectionMode(false);
    }
  };

  // Download selected
  const handleDownloadSelected = async () => {
    if (!album.allow_downloads) return;

    const selectedPhotosArray = photos.filter(p => selectedPhotoIds.has(p.id));
    if (selectedPhotosArray.length === 0) return;

    setDownloading(true);
    setDownloadProgress({ completed: 0, total: selectedPhotosArray.length });

    try {
      for (let i = 0; i < selectedPhotosArray.length; i++) {
        const photo = selectedPhotosArray[i];
        const ext = photo.media_type === 'video' ? 'mp4' : 'jpg';
        const filename = `${album.name || 'album'}_${i + 1}.${ext}`;
        await downloadFile(photo.cdnUrl, filename);
        setDownloadProgress({ completed: i + 1, total: selectedPhotosArray.length });
      }
      setSelectionMode(false);
      setSelectedPhotoIds(new Set());
    } catch (error) {
      console.error('Error downloading:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Get media count text
  const getMediaCountText = useCallback(() => {
    const photoCount = photos.filter(p => p.media_type !== 'video').length;
    const videoCount = photos.filter(p => p.media_type === 'video').length;
    if (videoCount === 0) return `${photoCount} ${photoCount === 1 ? 'Photo' : 'Photos'}`;
    if (photoCount === 0) return `${videoCount} ${videoCount === 1 ? 'Video' : 'Videos'}`;
    return `${photoCount} ${photoCount === 1 ? 'Photo' : 'Photos'}, ${videoCount} ${videoCount === 1 ? 'Video' : 'Videos'}`;
  }, [photos]);

  // Render branding section
  const renderBrandingSection = () => {
    if (!brandingInfo || !brandingInfo.album) return null;

    const { album: albumData, contributors } = brandingInfo;
    const creator = albumData.users;
    const studio = albumData.studios;
    const displayStudio = studio;
    const displayCreator = !studio ? creator : null;

    return (
      <div className="album-branding-section">
        <div className="branding-label">Created by</div>

        {displayStudio && (
          <div className="branding-card">
            <div className="branding-profile">
              {displayStudio.cover_image_cdn_url ? (
                <img src={displayStudio.cover_image_cdn_url} alt={displayStudio.name} className="branding-avatar" />
              ) : (
                <div className="branding-avatar-placeholder"><FiBriefcase /></div>
              )}
              <div className="branding-info">
                <div className="branding-name">{displayStudio.name}</div>
                <div className="branding-username">@{displayStudio.username}</div>
                {displayStudio.bio && <div className="branding-bio">{displayStudio.bio}</div>}
              </div>
            </div>
          </div>
        )}

        {displayCreator && (
          <div className="branding-card">
            <div className="branding-profile">
              {displayCreator.profile_photo_cdn_url ? (
                <img src={displayCreator.profile_photo_cdn_url} alt="" className="branding-avatar" />
              ) : (
                <div className="branding-avatar-placeholder"><FiUser /></div>
              )}
              <div className="branding-info">
                <div className="branding-name">
                  {displayCreator.display_name || `${displayCreator.first_name} ${displayCreator.last_name}`}
                </div>
                {displayCreator.username && (
                  <div className="branding-username">@{displayCreator.username}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {contributors && contributors.length > 0 && (
          <div className="contributors-section">
            <div className="contributors-title">Co-creators ({contributors.length})</div>
            {contributors.map((contributor) => (
              <div key={contributor.id} className="contributor-card">
                <div className="contributor-profile">
                  {contributor.users?.profile_photo_cdn_url ? (
                    <img src={contributor.users.profile_photo_cdn_url} alt="" className="contributor-avatar" />
                  ) : (
                    <div className="contributor-avatar-placeholder"><FiUser /></div>
                  )}
                  <div className="contributor-info">
                    <div className="contributor-name">
                      {contributor.users?.display_name ||
                        `${contributor.users?.first_name} ${contributor.users?.last_name}`}
                    </div>
                    {contributor.users?.username && (
                      <div className="contributor-username">@{contributor.users.username}</div>
                    )}
                    {contributor.contributor_type && (
                      <div className="contributor-role">
                        {contributor.contributor_type.charAt(0).toUpperCase() + contributor.contributor_type.slice(1)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="standalone-album-loading" style={{ minHeight: 300 }}>
        <div className="spinner" />
        <p>Loading album...</p>
      </div>
    );
  }

  return (
    <div className="standalone-album-view">
      {/* Expiry Warning */}
      {expiryDaysLeft !== null && (
        <div className="album-expiry-warning">
          <div className="expiry-content">
            <span className={`expiry-icon ${expiryDaysLeft === 0 ? 'expired' : ''}`}>
              {expiryDaysLeft === 0 ? <FiAlertTriangle /> : <FiClock />}
            </span>
            <span className={`expiry-text ${expiryDaysLeft === 0 ? 'expired' : ''}`}>
              {expiryDaysLeft === 0
                ? 'This album has expired!'
                : `Album expires in ${expiryDaysLeft} day${expiryDaysLeft !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
      )}

      {/* Branding Section */}
      {renderBrandingSection()}

      {/* Stats and Actions Row */}
      <div className="album-stats-row">
        <span className="stats-text">{getMediaCountText()}</span>
        <div className="stats-actions">
          {album.allow_downloads && (
            <button
              className={`icon-btn ${selectionMode ? 'active' : ''}`}
              onClick={toggleSelectionMode}
              title={selectionMode ? 'Cancel selection' : 'Select photos'}
            >
              {selectionMode ? <FiCheckCircle /> : <FiCheck />}
            </button>
          )}
        </div>
      </div>

      {/* Folder Navigation Breadcrumb */}
      {folderPath.length > 0 && (
        <div className="album-breadcrumb">
          <button className="breadcrumb-back" onClick={handleBackFromFolder}>
            <FiArrowLeft />
            <span>Back</span>
          </button>
          <span className="breadcrumb-path">
            / {folderPath.map(f => f.name).join(' / ')}
          </span>
        </div>
      )}

      {/* Folders Grid */}
      {viewMode === 'root' && folders.length > 0 && (
        <div className="album-folders-section">
          <div className="folders-title">Folders</div>
          {folders.map((folder) => (
            <div
              key={folder.id}
              className="folder-item"
              onClick={() => handleFolderSelect(folder)}
            >
              <div className="folder-icon"><FiFolder /></div>
              <div className="folder-info">
                <div className="folder-name">{folder.folder_name}</div>
                <div className="folder-meta">{folder.media_count || 0} items</div>
              </div>
              <span className="folder-arrow"><FiChevronRight /></span>
            </div>
          ))}
        </div>
      )}

      {/* Photos Section Title */}
      {photos.length > 0 && (
        <div className="album-photos-title">
          {folderPath.length > 0 ? 'Photos' : 'All Photos'}
        </div>
      )}

      {/* Photo Grid */}
      {photos.length > 0 ? (
        <>
          <div className="album-photo-grid">
            {photos.map((photo, index) => {
              const isSelected = selectedPhotoIds.has(photo.id);
              const isVideo = photo.media_type === 'video';
              const thumbnailUrl = isVideo && photo.thumbnail_cdn_url
                ? photo.thumbnail_cdn_url
                : (photo.thumbnail_cdn_url || photo.cdnUrl);

              return (
                <div
                  key={photo.id}
                  className={`album-photo-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handlePhotoPress(index)}
                  onContextMenu={(e) => {
                    if (!selectionMode && album.allow_downloads) {
                      e.preventDefault();
                      setSelectionMode(true);
                      togglePhotoSelection(photo.id);
                    }
                  }}
                >
                  <img
                    src={thumbnailUrl}
                    alt=""
                    loading="lazy"
                  />
                  {isVideo && (
                    <div className="video-overlay">
                      <div className="video-play-icon"><FiPlay /></div>
                    </div>
                  )}
                  {selectionMode && (
                    <div className="selection-checkbox">
                      <div className={`checkbox ${isSelected ? 'selected' : ''}`}>
                        {isSelected && <FiCheck />}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Load More */}
          {hasMorePhotos && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <button
                className="unlock-btn"
                style={{ maxWidth: 200, margin: '0 auto' }}
                onClick={loadMorePhotos}
              >
                Load More
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="album-empty">
          <span className="empty-icon"><FiImage /></span>
          <span className="empty-text">No photos in this album</span>
        </div>
      )}

      {/* Selection Actions Bar */}
      {selectionMode && selectedPhotoIds.size > 0 && (
        <div className="album-selection-bar">
          <div className="selection-bar-content">
            <span className="selection-count">{selectedPhotoIds.size} selected</span>
            <div className="selection-actions">
              <button className="selection-btn" onClick={handleDownloadSelected} disabled={downloading}>
                {downloading ? (
                  <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                ) : (
                  <>
                    <FiDownload />
                    <span>Download</span>
                  </>
                )}
              </button>
              <button
                className="selection-btn cancel"
                onClick={() => { setSelectionMode(false); setSelectedPhotoIds(new Set()); }}
              >
                <FiX />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Progress Modal */}
      {downloading && (
        <div className="album-download-progress">
          <div className="progress-content">
            <div className="spinner" />
            <div className="progress-text">
              Downloading... {downloadProgress.completed} of {downloadProgress.total}
            </div>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{ width: `${downloadProgress.total > 0 ? (downloadProgress.completed / downloadProgress.total) * 100 : 0}%` }}
              />
            </div>
            <button
              className="cancel-download-btn"
              onClick={() => setDownloading(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      <PhotoLightbox
        visible={viewerVisible}
        photos={photos}
        initialIndex={viewerIndex}
        onClose={() => setViewerVisible(false)}
        albumName={album.name}
      />
    </div>
  );
};

export default StandaloneAlbumView;
